import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Divider,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { DrawerActions } from '@/components/Drawer/DrawerActions';
import { LoadingButton } from '@mui/lab';
import { CouponListItem } from '@/models/coupon';
import { useNotification } from '@starjack/features/notifications';
import {
    useGetCouponDetailQuery,
    usePutCouponMutation,
    usePatchCouponActiveMutation,
} from '@/api/v2/coupon';
import { formatStarjackDateWithTime } from '@/utils/time';
import { DatePicker } from '@/components/DatePicker/DatePicker';
import { NumberInput } from '@/components/NumberInput/NumberInput';
import { MoneyInput } from '@/components/MoneyInput/MoneyInput';
import { CardListRow } from '@/components/Card/CardListRow';

type CouponDrawerContentProps = {
    // The paged list row — codes and redemptions arrive via the detail query.
    coupon: CouponListItem;
    onClose: () => void;
};

export const CouponEditDrawerContent: FunctionComponent<
    CouponDrawerContentProps
> = ({ coupon, onClose }) => {
    const { addNotification } = useNotification();
    const {
        data: couponDetail,
        isLoading: isDetailLoading,
        isError: isDetailError,
    } = useGetCouponDetailQuery(coupon.id);

    // The row prop is a snapshot from when the drawer opened. The detail query
    // refetches on every mutation, so it is the truth for anything mutable here.
    const currentCoupon = couponDetail ?? coupon;
    const [putCoupon, { isLoading: isSaving }] = usePutCouponMutation();
    const [patchActive] = usePatchCouponActiveMutation();

    const [validFrom, setValidFrom] = useState<Date | null>(
        coupon.validFrom ? new Date(coupon.validFrom) : null
    );
    const [validUntil, setValidUntil] = useState<Date>(
        new Date(coupon.validUntil)
    );
    const [amount, setAmount] = useState(coupon.amount);
    const [contingent, setContingent] = useState<number | null>(null);
    // null doubles as "not seeded yet" and "cleared by the user" — this flag keeps
    // the two apart, so a detail refetch never restores a deliberately emptied field.
    const [contingentTouched, setContingentTouched] = useState(false);
    const [newCodes, setNewCodes] = useState('');

    // The list row carries no codes, so the contingent arrives with the detail.
    useEffect(() => {
        if (!contingentTouched && couponDetail?.codes?.length) {
            setContingent(couponDetail.codes[0].contingent);
        }
    }, [couponDetail, contingentTouched]);

    const onToggleActive = () => {
        const wasActive = currentCoupon.active;
        patchActive({ id: coupon.id, active: !wasActive })
            .unwrap()
            .then(() => {
                addNotification({
                    message: wasActive
                        ? 'Coupon deaktiviert'
                        : 'Coupon aktiviert',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message: 'Fehler beim Ändern des Status',
                    type: 'error',
                });
            });
    };

    const onSave = () => {
        putCoupon({
            id: coupon.id,
            body: {
                validFrom,
                validUntil,
                amount,
                contingent,
                newCodes: newCodes || undefined,
            },
        })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Coupon gespeichert',
                    type: 'success',
                });
                onClose();
            })
            .catch(() => {
                addNotification({
                    message: 'Fehler beim Speichern',
                    type: 'error',
                });
            });
    };

    const codes = couponDetail?.codes ?? [];

    const isValid =
        validUntil instanceof Date &&
        !isNaN(validUntil.getTime()) &&
        (!validFrom || validFrom <= validUntil) &&
        !!amount?.amount &&
        amount.amount > 0 &&
        (contingent === null || contingent >= 1) &&
        // Until the detail arrived the contingent is unknown — saving would silently
        // send "no change" while the field still looks empty.
        !isDetailLoading;

    return (
        <Box sx={{ width: '720px' }}>
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>{coupon.name}</Typography>
            </Box>

            <Stack sx={{ p: 4 }} spacing={3}>
                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                >
                    <Typography variant='body1' fontWeight={500}>
                        Status
                    </Typography>
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography variant='body2'>
                            {currentCoupon.active ? 'Aktiv' : 'Inaktiv'}
                        </Typography>
                        <Switch
                            checked={currentCoupon.active}
                            onChange={onToggleActive}
                        />
                    </Stack>
                </Stack>

                <Divider />

                <DatePicker
                    onChange={(nextDate) => setValidFrom(nextDate)}
                    value={validFrom}
                    label='Gültig ab (optional)'
                />

                <DatePicker
                    onChange={(nextDate) => setValidUntil(nextDate)}
                    value={validUntil}
                    label='Gültig bis'
                />

                <MoneyInput
                    onChange={(nextValue) =>
                        setAmount({
                            amount: nextValue?.amount,
                            currency: 'EUR',
                        })
                    }
                    value={amount}
                    label='Betrag'
                />

                <NumberInput
                    onChange={(nextValue) => {
                        setContingentTouched(true);
                        setContingent(nextValue);
                    }}
                    value={contingent}
                    label='Kontingent (pro Code)'
                />

                <Divider />

                <Box>
                    <Typography variant='body1' fontWeight={500}>
                        Bergbahn
                    </Typography>
                    <Typography
                        variant='body2'
                        sx={{ mt: 1, color: 'text.secondary' }}
                    >
                        {coupon.liftOperatorName ||
                            (coupon.liftOperatorId
                                ? 'Unbekannte Bergbahn'
                                : 'Global')}
                    </Typography>
                </Box>

                <Divider />

                <TextField
                    label='Neue Codes hinzufügen'
                    value={newCodes}
                    onChange={(e) => setNewCodes(e.target.value)}
                    rows={3}
                    multiline
                    helperText='Codes mit Leerzeichen oder Zeilenumbrüchen trennen'
                />

                <DrawerActions>
                    <Stack
                        sx={{ alignItems: 'center' }}
                        spacing={2}
                        direction='row'
                    >
                        <Button variant='outlined' onClick={onClose}>
                            Abbrechen
                        </Button>
                        <LoadingButton
                            variant='contained'
                            onClick={onSave}
                            loading={isSaving}
                            disabled={!isValid}
                        >
                            Speichern
                        </LoadingButton>
                    </Stack>
                </DrawerActions>

                <Divider />

                <Typography variant='h6'>Codes & Einlösungen</Typography>

                {isDetailError ? (
                    <Typography variant='body2' color='error'>
                        Codes und Einlösungen konnten nicht geladen werden.
                    </Typography>
                ) : isDetailLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 4,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Card elevation={2}>
                        <CardListRow
                            sx={{
                                backgroundColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography sx={{ width: '33%', fontWeight: 600 }}>
                                Code
                            </Typography>
                            <Typography sx={{ width: '22%', fontWeight: 600 }}>
                                Kontingent
                            </Typography>
                            <Typography sx={{ width: '22%', fontWeight: 600 }}>
                                Verbraucht
                            </Typography>
                            <Typography sx={{ width: '23%', fontWeight: 600 }}>
                                Details
                            </Typography>
                        </CardListRow>

                        {codes.map((code, index) => (
                            <Fragment key={code.id}>
                                {index !== 0 && (
                                    <Divider sx={{ width: '100%' }} />
                                )}
                                <CardListRow
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Typography sx={{ width: '33%' }}>
                                        {code.code}
                                    </Typography>
                                    <Typography sx={{ width: '22%' }}>
                                        {code.contingent}
                                    </Typography>
                                    <Typography sx={{ width: '22%' }}>
                                        {code.used}
                                    </Typography>
                                    <Box sx={{ width: '23%' }}>
                                        {code.usages &&
                                        code.usages.length > 0 ? (
                                            code.usages.map((usage) => (
                                                <Typography
                                                    key={usage.id}
                                                    variant='caption'
                                                    sx={{
                                                        display: 'block',
                                                    }}
                                                >
                                                    {usage.customerEmail ||
                                                        usage.customerId}{' '}
                                                    (
                                                    {formatStarjackDateWithTime(
                                                        new Date(usage.usedAt)
                                                    )}
                                                    )
                                                </Typography>
                                            ))
                                        ) : (
                                            <Typography
                                                variant='caption'
                                                color='text.secondary'
                                            >
                                                {isDetailError
                                                    ? 'Einlösungen konnten nicht geladen werden'
                                                    : 'Noch nicht eingelöst'}
                                            </Typography>
                                        )}
                                    </Box>
                                </CardListRow>
                            </Fragment>
                        ))}
                    </Card>
                )}
            </Stack>
        </Box>
    );
};
